import { type Action, createStore } from 'react-sweet-state';
import { produce } from 'immer';

import {
  COMPLIANCE_ENTITY,
  type ComplianceEntity,
  type EntityComplianceData,
  isAppliedComplianceStatus,
  isCompletedComplianceStatus,
} from 'src/data/compliance';
import {
  queryComplianceForBlogDraft,
  queryComplianceForLongFormAssets,
  queryComplianceForVariations,
} from 'src/graphql/compliance';
import { type ID, isNegativeId } from 'src/lib/collection';
import { captureMessage } from 'src/lib/sentry';

interface PrePollingState {
  prePollingVariationIds: Set<ID>;
  prePollingLongFormAssetIds: Set<ID>;
  prePollingBlogDraftIds: Set<ID>;
}
interface PollingState {
  pollingVariationIds: Set<ID>;
  pollingLongFormAssetIds: Set<ID>;
  pollingBlogDraftIds: Set<ID>;
}
interface ResultState {
  variationResults: EntityComplianceData[];
  longFormAssetResults: EntityComplianceData[];
  blogDraftResults: EntityComplianceData[];
}

export type State = PrePollingState & PollingState & ResultState;
export type Actions = typeof ACTIONS;

const INITIAL_STATE: State = {
  prePollingVariationIds: new Set(),
  pollingVariationIds: new Set(),
  variationResults: [],

  prePollingLongFormAssetIds: new Set(),
  pollingLongFormAssetIds: new Set(),
  longFormAssetResults: [],

  prePollingBlogDraftIds: new Set(),
  pollingBlogDraftIds: new Set(),
  blogDraftResults: [],
};

const PRIVATE_ACTIONS = {
  addIdToPrePolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ getState, setState }): void => {
      const field = getPrePollingStateField(entity);
      const newValue = produce(getState()[field], (draft) => {
        draft.add(id);
      });
      setState({ [field]: newValue });
    },

  removeIdFromPrePolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ getState, setState }): void => {
      const field = getPrePollingStateField(entity);
      const newValue = produce(getState()[field], (draft) => {
        draft.delete(id);
      });
      setState({ [field]: newValue });
    },

  addIdToPolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ getState, setState }): void => {
      const field = getPollingStateField(entity);
      const newValue = produce(getState()[field], (draft) => {
        draft.add(id);
      });
      setState({ [field]: newValue });
    },

  removeIdFromPolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ getState, setState }): void => {
      const field = getPollingStateField(entity);
      const newValue = produce(getState()[field], (draft) => {
        draft.delete(id);
      });
      setState({ [field]: newValue });
    },

  // Polling runs on an interval, so these two write on every tick. They go through `produce` and
  // touch the draft only where something actually differs, which leaves the state identity intact
  // for an unchanged result — a new array or Set would re-render every subscriber every few
  // seconds, and one of those subscribers is an open editor.
  mergeResultsInCache:
    (entity: ComplianceEntity, itemsToMerge: EntityComplianceData[]): Action<State> =>
    ({ getState, setState }) => {
      const field = getResultsStateField(entity);
      const list = getState()[field] as EntityComplianceData[];

      const newList = produce(list, (draft) => {
        itemsToMerge.forEach((item) => {
          const existing = draft.find((entry) => entry.id === item.id);
          if (existing) {
            Object.assign(existing, item);
            return;
          }
          draft.push(item);
        });
      });

      setState({ [field]: newList });
    },

  removeCompletedIdsFromPolling:
    (entity: ComplianceEntity): Action<State> =>
    ({ getState, setState }): void => {
      const prePollingField = getPrePollingStateField(entity);
      const pollingField = getPollingStateField(entity);
      const resultsField = getResultsStateField(entity);
      const {
        [prePollingField]: prePollingIds,
        [pollingField]: pollingIds,
        [resultsField]: results,
      } = getState();

      const completedIds = getCompletedIds(results);
      const withoutCompleted = (ids: Set<ID>) =>
        produce(ids, (draft) => {
          completedIds.forEach((id) => draft.delete(id));
        });

      setState({
        [prePollingField]: withoutCompleted(prePollingIds),
        [pollingField]: withoutCompleted(pollingIds),
      });
    },
};

const ACTIONS = {
  /**
   * Add entity ID to a polling list for compliance status.
   * @param entity
   * @param id
   */
  addToPolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ dispatch }): void => {
      // we use "negative" IDs for some dummy/skeleton entities, no need to check compliance for those
      if (id == null || isNegativeId(id)) {
        return;
      }

      // if entity gets added to polling again, then cached result is stale - remove it
      dispatch(ACTIONS.removeCachedResult(entity, id));

      // include entity ID in the "polling" list and remove it from "pre-polling" list
      dispatch(PRIVATE_ACTIONS.addIdToPolling(entity, id));
      dispatch(PRIVATE_ACTIONS.removeIdFromPrePolling(entity, id));
    },

  /**
   * Mark entity for "compliance" checking, usually right after user starts editing it,
   * but actual polling should start after BE confirms that entity was saved
   * @param entity
   * @param id
   */
  addToPrePolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ dispatch }): void => {
      // we use "negative" IDs for some dummy/skeleton entities, no need to check compliance for those
      if (id == null || isNegativeId(id)) {
        return;
      }

      // if entity gets added to polling again, then cached result is stale - remove it
      dispatch(ACTIONS.removeCachedResult(entity, id));

      // include entity ID in the "pre-polling" list and remove it from "polling" list
      dispatch(PRIVATE_ACTIONS.removeIdFromPolling(entity, id));
      dispatch(PRIVATE_ACTIONS.addIdToPrePolling(entity, id));
    },

  /**
   * Remove entity ID from all polling lists.
   * @param entity
   * @param id
   */
  removeFromPolling:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ dispatch }): void => {
      dispatch(PRIVATE_ACTIONS.removeIdFromPolling(entity, id));
      dispatch(PRIVATE_ACTIONS.removeIdFromPrePolling(entity, id));
    },

  /**
   * Poll compliance status for entities that are currently in the polling list.
   */
  pollCompliance:
    (entity: ComplianceEntity): Action<State> =>
    async ({ getState, dispatch }): Promise<void> => {
      const field = getPollingStateField(entity);
      const { [field]: pollingIds } = getState();
      if (pollingIds.size === 0) {
        return;
      }

      try {
        const results = await pollResults(entity, [...pollingIds]);
        if (!results) {
          return;
        }

        dispatch(PRIVATE_ACTIONS.mergeResultsInCache(entity, results));
        dispatch(PRIVATE_ACTIONS.removeCompletedIdsFromPolling(entity));
      } catch (error) {
        captureMessage('Error while checking compliance', {
          level: 'warning',
          extra: {
            error: error instanceof Error ? error.toString() : 'unknown',
            source: 'ComplianceStore -> pollCompliance',
          },
        });
      }
    },

  /**
   * Remove cached compliance result for an entity.
   * @param entity
   * @param id
   */
  removeCachedResult:
    (entity: ComplianceEntity, id: ID): Action<State> =>
    ({ getState, setState }): void => {
      const field = getResultsStateField(entity);
      const newValue = produce(getState()[field], (draft) => {
        const index = draft.findIndex((item) => item.id === id);
        if (index !== -1) {
          draft.splice(index, 1);
        }
      });
      setState({ [field]: newValue });
    },
};

export const ComplianceStore = createStore<State, Actions>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'compliance-store',
});

const getCompletedIds = (results: EntityComplianceData[]): ID[] =>
  results
    .filter(
      (item) =>
        isCompletedComplianceStatus(item.complianceStatus) ||
        isAppliedComplianceStatus(item.complianceStatus)
    )
    .map(({ id }) => id);

const pollResults = (
  entity: ComplianceEntity,
  ids: ID[]
): Promise<EntityComplianceData[] | undefined> => {
  switch (entity) {
    case COMPLIANCE_ENTITY.variation:
      return queryComplianceForVariations({ variationIds: ids.map(String) });

    case COMPLIANCE_ENTITY.longFormAsset:
      return queryComplianceForLongFormAssets({ longFormAssetIds: ids.map(String) });

    case COMPLIANCE_ENTITY.blogDraft: {
      // currently we only support checking one draft at a time
      return queryComplianceForBlogDraft({ id: String(ids[0]) }).then((result) =>
        result ? [result] : undefined
      );
    }

    default:
      throw new Error(`Unknown entity type: ${entity}`);
  }
};

const getPrePollingStateField = (entity: ComplianceEntity): keyof PrePollingState => {
  switch (entity) {
    case COMPLIANCE_ENTITY.variation:
      return 'prePollingVariationIds';
    case COMPLIANCE_ENTITY.longFormAsset:
      return 'prePollingLongFormAssetIds';
    case COMPLIANCE_ENTITY.blogDraft:
      return 'prePollingBlogDraftIds';
    default:
      throw new Error(`Unknown entity type: ${entity}`);
  }
};

const getPollingStateField = (entity: ComplianceEntity): keyof PollingState => {
  switch (entity) {
    case COMPLIANCE_ENTITY.variation:
      return 'pollingVariationIds';
    case COMPLIANCE_ENTITY.longFormAsset:
      return 'pollingLongFormAssetIds';
    case COMPLIANCE_ENTITY.blogDraft:
      return 'pollingBlogDraftIds';
    default:
      throw new Error(`Unknown entity type: ${entity}`);
  }
};

const getResultsStateField = (entity: ComplianceEntity): keyof ResultState => {
  switch (entity) {
    case COMPLIANCE_ENTITY.variation:
      return 'variationResults';
    case COMPLIANCE_ENTITY.longFormAsset:
      return 'longFormAssetResults';
    case COMPLIANCE_ENTITY.blogDraft:
      return 'blogDraftResults';
    default:
      throw new Error(`Unknown entity type: ${entity}`);
  }
};
